import React, { useState } from "react";
import { Settings, Bell, Moon, Shield, ChevronRight, LogOut } from "lucide-react";
import PageBreathingAura from "@/components/PageBreathingAura";

const AVATAR_URL = "https://images.unsplash.com/photo-1774897778836-3b13763e71b3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwyfHxwcm9maWxlJTIwcG9ydHJhaXQlMjBwcm9mZXNzaW9uYWwlMjBkYXJrJTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3NzUyMjgyMzl8MA&ixlib=rb-4.1.0&q=85&w=200";

const userStats = [
  { label: "Days Active", value: "47" },
  { label: "Sessions", value: "124" },
  { label: "Avg Score", value: "84" },
  { label: "Best Streak", value: "12" },
];

const Toggle = ({ enabled, onToggle }) => (
  <button
    onClick={onToggle}
    className="relative w-11 h-6 rounded-full"
    style={{
      background: enabled ? '#F59E0B' : 'rgba(255,255,255,0.1)',
      transition: 'background 0.2s ease',
    }}
  >
    <div
      className="absolute top-0.5 w-5 h-5 rounded-full bg-black"
      style={{
        left: enabled ? '22px' : '2px',
        transition: 'left 0.2s ease',
      }}
    />
  </button>
);

export const ProfileTab = () => {
  const [notifications, setNotifications] = useState(true);
  const [darkMode, setDarkMode] = useState(true);
  const [privacy, setPrivacy] = useState(false);

  const settingItems = [
    { icon: Bell, label: "Notifications", toggle: true, value: notifications, onToggle: () => setNotifications(v => !v) },
    { icon: Moon, label: "Dark Mode", toggle: true, value: darkMode, onToggle: () => setDarkMode(v => !v) },
    { icon: Shield, label: "Privacy Mode", toggle: true, value: privacy, onToggle: () => setPrivacy(v => !v) },
    { icon: Settings, label: "App Settings", toggle: false },
  ];

  return (
    <div
      data-testid="profile-tab"
      className="flex h-full min-h-0 flex-col gap-6 overflow-y-auto px-4 pt-10 pb-24 relative z-10"
      style={{
        animation: 'fadeIn 0.4s ease-out',
        overscrollBehavior: 'contain',
        WebkitOverflowScrolling: 'touch',
      }}
    >
      <PageBreathingAura variant="profile" />

      {/* Avatar + Name */}
      <div
        className="relative z-10 flex flex-col items-center gap-3"
        style={{ animation: 'fadeInUp 0.5s ease-out' }}
      >
        <div
          className="w-20 h-20 rounded-full overflow-hidden"
          style={{
            border: '2px solid rgba(245,158,11,0.4)',
            boxShadow: '0 0 0 10px rgba(168,85,247,0.06), 0 24px 42px rgba(0,0,0,0.24)',
            animation: 'breatheGradient 8s ease-in-out infinite',
          }}
        >
          <img
            data-testid="profile-avatar"
            src={AVATAR_URL}
            alt="Profile"
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </div>
        <div className="text-center">
          <h1 className="text-xl font-bold text-white">Alex Chen</h1>
          <p className="text-xs text-zinc-500 mt-0.5">Premium Member</p>
        </div>
      </div>

      {/* Stats Grid */}
      <div
        className="relative z-10 grid grid-cols-4 gap-2"
        style={{ animation: 'fadeInUp 0.6s ease-out' }}
      >
        {userStats.map((stat, idx) => (
          <div
            key={stat.label}
            data-testid={`profile-stat-${stat.label.toLowerCase().replace(' ', '-')}`}
            className="flex flex-col items-center p-3 rounded-xl"
            style={{
              background: 'linear-gradient(180deg, rgba(17,17,17,0.95) 0%, rgba(10,10,10,0.92) 100%)',
              border: '1px solid rgba(255,255,255,0.05)',
              animation: `fadeInUp 0.55s cubic-bezier(0.22, 1, 0.36, 1) ${0.12 + idx * 0.07}s both`,
              boxShadow: '0 18px 40px rgba(0,0,0,0.2)',
            }}
          >
            <span className="text-lg font-bold text-white">{stat.value}</span>
            <span className="text-[9px] text-zinc-600 mt-0.5 text-center leading-tight">{stat.label}</span>
          </div>
        ))}
      </div>

      {/* Settings */}
      <div className="relative z-10" style={{ animation: 'fadeInUp 0.7s ease-out' }}>
        <h3 className="text-sm font-medium text-zinc-400 mb-3 tracking-wide">Settings</h3>
        <div
          className="rounded-2xl overflow-hidden"
          style={{
            background: 'linear-gradient(180deg, rgba(16,16,16,0.95) 0%, rgba(10,10,10,0.92) 100%)',
            border: '1px solid rgba(255,255,255,0.05)',
            boxShadow: '0 24px 56px rgba(0,0,0,0.22)',
          }}
        >
          {settingItems.map((item, idx) => {
            const Icon = item.icon;
            return (
              <div
                key={item.label}
                data-testid={`setting-${item.label.toLowerCase().replace(' ', '-')}`}
                className="flex items-center gap-3 px-4 py-3.5"
                style={{
                  borderBottom: idx < settingItems.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none',
                  animation: `fadeInUp 0.5s cubic-bezier(0.22, 1, 0.36, 1) ${0.18 + idx * 0.06}s both`,
                }}
              >
                <Icon size={16} style={{ color: '#71717A' }} />
                <span className="flex-1 text-sm text-white">{item.label}</span>
                {item.toggle ? (
                  <Toggle enabled={item.value} onToggle={item.onToggle} />
                ) : (
                  <ChevronRight size={16} style={{ color: '#3F3F46' }} />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Logout */}
      <button
        data-testid="logout-btn"
        className="relative z-10 flex items-center justify-center gap-2 py-3 rounded-xl w-full"
        style={{
          background: 'rgba(239,68,68,0.08)',
          border: '1px solid rgba(239,68,68,0.15)',
          animation: 'fadeInUp 0.8s ease-out',
          boxShadow: '0 20px 44px rgba(0,0,0,0.2)',
        }}
      >
        <LogOut size={16} style={{ color: '#EF4444' }} />
        <span className="text-sm" style={{ color: '#EF4444' }}>Log Out</span>
      </button>
    </div>
  );
};

export default ProfileTab;
